<!-- @components/messenger/StickerPicker.svelte -->
<script>
  import { fade } from 'svelte/transition';
  import { onMount } from 'svelte';
  import { dndzone } from 'svelte-dnd-action';
  import { normalizeUrl } from '$lib/utils';

  import {
    getStickerPacks,
    getMyStickerPacks,
    getStickers,
    downloadPack,
    savePackSort,
    deletePack,
    selectedStickerPack,
    stickerCache,
    recentPack,
    getRecentByUser,
    setRecentByUser
  } from '$lib/stickers';

  import { user } from '$lib/stores/store';
  import { get } from 'svelte/store';

  export let onSelect = null;
  export let onClose = null;

  /* ======================================================
     🕘 RECENT (UI ONLY)
  ====================================================== */

  function loadRecentPack() {
    recentPack.stickers = getRecentByUser();
  }

  function saveRecentPack() {
    setRecentByUser(recentPack.stickers);
  }

  /* ======================================================
     📦 STATE
  ====================================================== */
  let allPacks = [];
  let myPacks = []; // [recent, ...dbPacks]
  let stickers = []; // grid render
  let selectedPack = null;

  let showSetting = false;
  let settingTab = 'add';
  let confirmDeletePack = null;
  let refreshTimer = null;

  function isImageAsset(value) {
    return /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(String(value || '').split(/[?#]/, 1)[0]);
  }

  // chỉ pack thật (DB)
  $: sortablePacks = myPacks.filter((p) => p.id !== 'recent');

  /* ======================================================
     ⚡ STICKER CACHE
  ====================================================== */
  async function loadStickersCached(packId) {
    const cache = get(stickerCache);

    if (cache[packId]) {
      stickers = cache[packId];
      return;
    }

    const data = await getStickers(packId);

    stickerCache.update((c) => ({
      ...c,
      [packId]: data
    }));

    stickers = data;
  }

  /* ======================================================
     📦 LOAD PACKS
  ====================================================== */
  async function loadMyPacks() {
    const packsFromApi = await getMyStickerPacks();
    myPacks = [recentPack, ...packsFromApi];

    const savedId = get(selectedStickerPack);
    let found = myPacks.find((p) => p.id === savedId) || recentPack;
    selectPack(found, false);
  }

  async function refreshStickerData() {
    stickerCache.set({});
    loadRecentPack();
    allPacks = await getStickerPacks();
    await loadMyPacks();
  }

  function scheduleStickerRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshStickerData().catch(console.error);
    }, 120);
  }

  function handleStickerStorage(event) {
    if (event.key === 'k2:stickers-version') {
      scheduleStickerRefresh();
    }
  }

  /* ======================================================
     👉 SELECT PACK
  ====================================================== */
  function selectPack(pack, save = true) {
    selectedPack = pack;
    showSetting = false;

    if (save) selectedStickerPack.set(pack.id);

    if (pack.id === 'recent') {
      stickers = recentPack.stickers;
    } else {
      loadStickersCached(pack.id);
    }
  }

  /* ======================================================
     👉 SELECT STICKER
  ====================================================== */
  function handleSelect(sticker) {
    recentPack.stickers = [sticker, ...recentPack.stickers.filter((s) => s.id !== sticker.id)].slice(0, 20);

    saveRecentPack();

    if (get(selectedStickerPack) === 'recent') {
      stickers = recentPack.stickers;
    }

    onSelect?.(sticker);
  }

  /* ======================================================
     ⬇️ DOWNLOAD
  ====================================================== */
  async function handleDownload(pack) {
    await downloadPack(pack.id);
    allPacks = await getStickerPacks();
    await loadMyPacks();
    await loadStickersCached(pack.id); // preload
    scheduleStickerRefresh();
  }

  /* ======================================================
     🔀 SORT (NO RECENT)
  ====================================================== */
  function handleConsider(e) {
    sortablePacks = e.detail.items;
    myPacks = [recentPack, ...sortablePacks];
  }

  async function handleFinalize(e) {
    sortablePacks = e.detail.items;
    myPacks = [recentPack, ...sortablePacks];

    const orders = sortablePacks.map((p, i) => ({
      pack_id: p.id,
      sort_order: i
    }));

    await savePackSort(orders);
  }

  /* ======================================================
     🗑 DELETE
  ====================================================== */
  async function doDeletePack() {
    if (!confirmDeletePack) return;

    await deletePack(confirmDeletePack.id);

    // clear cache
    stickerCache.update((c) => {
      const { [confirmDeletePack.id]: _, ...rest } = c;
      return rest;
    });

    confirmDeletePack = null;
    await loadMyPacks();
    scheduleStickerRefresh();
  }

  /* ======================================================
     🧠 MISC
  ====================================================== */
  function handleClickOutside(e) {
    if (!e.target.closest('.sticker-picker')) onClose?.();
  }

  const openSetting = () => {
    showSetting = !showSetting;
    settingTab = 'add';
  };

  /* ======================================================
     🚀 INIT
  ====================================================== */
  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('k2:stickers-changed', scheduleStickerRefresh);
    window.addEventListener('storage', handleStickerStorage);

    loadRecentPack();
    getStickerPacks()
      .then((packs) => {
        allPacks = packs;
        return loadMyPacks();
      })
      .catch(console.error);

    return () => {
      clearTimeout(refreshTimer);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('k2:stickers-changed', scheduleStickerRefresh);
      window.removeEventListener('storage', handleStickerStorage);
    };
  });
</script>

<!-- ======================================================
     UI
====================================================== -->
<div
  class="sticker-picker absolute bottom-full left-0 mb-2 w-80 bg-white rounded-xl shadow-xl border border-gray-300 overflow-hidden z-50"
  transition:fade={{ duration: 150 }}>
  {#if showSetting}
    <!-- SETTINGS -->
    <div class="h-64 flex flex-col">
      <div class="flex border-b border-gray-300 bg-gray-100">
        <button
          class="flex-1 {settingTab === 'add' ? 'bg-white font-medium' : 'hover:bg-gray-200'}"
          on:click={() => (settingTab = 'add')}>
          ➕ Thêm
        </button>
        <button
          class="flex-1 {settingTab === 'sort' ? 'bg-white font-medium' : 'hover:bg-gray-200'}"
          on:click={() => (settingTab = 'sort')}>
          🔀 Sắp xếp
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-3">
        {#if settingTab === 'add'}
          {#each allPacks as pack}
            <div class="flex items-center gap-2 border border-gray-300 rounded p-2 mb-2">
              {#if isImageAsset(pack.icon)}
                <img src={normalizeUrl(pack.icon)} alt={pack.name} class="w-8 h-8 object-contain shrink-0" />
              {:else}
                <span class="text-xl">{pack.icon}</span>
              {/if}
              <span class="flex-1">{pack.name}</span>
              {#if pack.downloaded}
                <span class="text-green-600">✓</span>
              {:else}
                <button class="px-2 py-1 text-xs bg-blue-500 text-white rounded" on:click={() => handleDownload(pack)}>
                  Tải
                </button>
              {/if}
            </div>
          {/each}
        {:else}
          <div
            use:dndzone={{ items: sortablePacks, dragHandleSelectors: ['.drag'] }}
            on:consider={handleConsider}
            on:finalize={handleFinalize}
            class="space-y-2">
            {#each sortablePacks as pack (pack.id)}
              <div class="flex items-center gap-2 border border-gray-300 rounded p-2">
                <span class="drag cursor-grab">☰</span>
                {#if isImageAsset(pack.icon)}
                  <img src={normalizeUrl(pack.icon)} alt={pack.name} class="w-8 h-8 object-contain shrink-0" />
                {:else}
                  <span class="text-xl">{pack.icon}</span>
                {/if}
                <span class="flex-1">{pack.name}</span>
                <button class="text-red-600" on:click={() => (confirmDeletePack = pack)}>🗑</button>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  {:else}
    <!-- STICKERS -->
    <div class="grid grid-cols-5 gap-2 p-3 max-h-64 overflow-y-auto">
      {#each stickers as s (s.id)}
        <button on:click={() => handleSelect(s)}>
          <img src={normalizeUrl(s.url)} class="w-full h-full object-contain" />
        </button>
      {/each}
    </div>
  {/if}

  <!-- BOTTOM BAR -->
  <div class="flex gap-2 border-t border-gray-300 p-2 bg-gray-50 overflow-x-auto">
    {#each myPacks as pack}
      <button
        class="w-10 h-10 border rounded
        {selectedPack?.id === pack.id ? 'bg-blue-100' : ''}"
        on:click={() => selectPack(pack)}
        title={pack.description || pack.name}>
        {#if isImageAsset(pack.icon)}
          <img src={normalizeUrl(pack.icon)} alt={pack.name} class="w-full h-full object-contain p-1" />
        {:else}
          {pack.icon}
        {/if}
      </button>
    {/each}
    <div class="flex-1"></div>
    <button class="w-10 h-10 border rounded" on:click={openSetting}>⚙️</button>
  </div>
</div>

{#if confirmDeletePack}
  <div class="fixed inset-0 bg-black/30 flex items-center justify-center">
    <div class="bg-white p-4 rounded border border-gray-300">
      <p class="mb-3">Xoá "{confirmDeletePack.name}"?</p>
      <div class="flex justify-end gap-2">
        <button on:click={() => (confirmDeletePack = null)}>Huỷ</button>
        <button class="bg-red-500 text-white px-3 py-1 rounded" on:click={doDeletePack}>Xoá</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .sticker-picker {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  }
</style>
